import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PoweredByLabel } from '@/components/widget-components'
import { LoadingIcon } from '@/components/LoadingIcon'
import { useAuth } from '@/contexts/auth-context'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { UploadFileComp } from '@/panels/signup/ManualCheck/UploadFile'
import { WidgetLayout } from '@/pages/Buy/WidgetLayout'
import { ImageData } from '@/requests/signup'
import { uploadFile } from '@/requests/upload'
import { DarkModeMenu } from '@/panels/signup/DarkmodeSignUpPanel'

const Upload = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { isAuth, loading: isAuthLoading } = useAuth()
  const {
    data: currentUser,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useCurrentUser(isAuth)
  const [error, setError] = useState('')
  const [fileUploadSuccess, setFileUploadSuccess] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const isInIframe = window.self !== window.top

  const upload = async (fileData: ImageData) => {
    if (!currentUser || isUploading) {
      return
    }

    setIsUploading(true)
    setError('')
    try {
      const { response, error } = await uploadFile(currentUser.id, [fileData])
      if (response) {
        setFileUploadSuccess(true)
      }
      if (error) {
        setError(t('upload.error.message'))
      }
    } finally {
      setIsUploading(false)
    }
  }

  if (isAuthLoading || isUserLoading) {
    return (
      <div className="my-auto flex justify-center">
        <LoadingIcon />
      </div>
    )
  }

  if (!isAuth) {
    return <Navigate to="/EasyLogin" replace state={{ from: location }} />
  }

  if (isUserError || !currentUser) {
    return (
      <WidgetLayout showInteracLabel={false} showPoweredByLabel={false}>
        <DarkModeMenu showSignOutButton={isAuth} />
        <div
          className="my-auto flex justify-center text-rose-600"
          data-testid="error-user-load"
        >
          {t('upload.error.userLoad')}
        </div>
      </WidgetLayout>
    )
  }

  if (fileUploadSuccess) {
    return (
      <WidgetLayout showInteracLabel={false} showPoweredByLabel={false}>
        <DarkModeMenu showSignOutButton={isAuth} />
        <div
          className="my-auto flex flex-col justify-center"
          style={{ paddingBottom: '400px' }}
          data-testid="container-upload-success"
        >
          <p
            className="text-center mt-40 mb-10"
            data-testid="text-upload-success-message"
          >
            {t('upload.success.message')}
          </p>
          <a
            href="/upload"
            className="mt-4 bg-black text-white dark:bg-semiWhite dark:text-black py-8 px-4 border-black w-full border-2 items-center text-center flex justify-center"
            data-testid="link-upload-another"
          >
            {t('upload.success.uploadAnother')}
          </a>
        </div>
        <PoweredByLabel />
      </WidgetLayout>
    )
  }

  return (
    <WidgetLayout showInteracLabel={false} showPoweredByLabel={true}>
      <DarkModeMenu showSignOutButton={isAuth} />
      <div
        className="flex items-center justify-center min-h-screen overflow-hidden"
        data-testid="container-upload"
      >
        <div
          className="w-full dark:text-white py-0"
          style={{
            position: 'absolute',
            top: !isInIframe ? '9%' : '2%',
            width: '90%',
          }}
          data-testid="container-upload-form"
        >
          <div
            className="text-lg font-normal uppercase justify-center items-center text-center"
            style={{ paddingTop: '5rem', paddingBottom: '30px' }}
            data-testid="text-upload-title"
          >
            {t('upload.title')}
          </div>
          <div className="space-y-4"></div>
          <div className="mb-12">
            <UploadFileComp onConfirm={upload} isUploading={isUploading} />
            {error && (
              <div className="text-rose-600" data-testid="error-upload-message">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </WidgetLayout>
  )
}

export default Upload
